
import React, { useEffect } from "react";
import {
  Mail, Phone, MapPin, ArrowUpRight, Wallet,
  Landmark, Receipt, Calculator, FileCheck,
  ShieldCheck, TrendingUp, PieChart, Target,
  Eye, Lightbulb, Sparkles, ChevronRight,
  CreditCard, ClipboardList, BadgeCheck,
  Building2, HandCoins, CircleDollarSign
} from "lucide-react";
import { motion } from "framer-motion";
import LogoFallback from "/assets/img/bubbanlogo.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" }
  }
};

const services = [
  {
    icon: Calculator,
    title: "Financial Management",
    description: "Supporting the university's financial planning, administration and management."
  },
  {
    icon: Receipt,
    title: "Accounts Management",
    description: "Maintaining financial records, accounts and institutional transactions."
  },
  {
    icon: CreditCard,
    title: "Fee & Payment Management",
    description: "Facilitating student fee collection and payment-related activities."
  },
  {
    icon: FileCheck,
    title: "Financial Documentation",
    description: "Maintaining organized financial documents, reports and statements."
  },
  {
    icon: PieChart,
    title: "Budget & Reporting",
    description: "Supporting budget preparation, financial reporting and expenditure monitoring."
  },
  {
    icon: ShieldCheck,
    title: "Financial Accountability",
    description: "Promoting transparency, accuracy and accountability in financial operations."
  }
];

const principles = [
  {
    icon: Eye,
    title: "Our Vision",
    color: "bg-blue-600",
    description: "To become a leading institution of higher learning in Asia."
  },
  {
    icon: Target,
    title: "Our Mission",
    color: "bg-emerald-600",
    description: "To nurture creativity, research, and leadership through quality education."
  },
  {
    icon: Lightbulb,
    title: "Our Goals",
    color: "bg-amber-500",
    description: "Expanding research opportunities and strengthening global partnerships."
  }
];

export default function Acfd({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Accounts and Finance Division | Bandarban University"
}) {
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <div className="bg-[#f7f9f7] text-gray-800 overflow-hidden">

      {/* HERO SECTION */}
      <section className="relative min-h-[490px] bg-[#073b2a] text-white overflow-hidden flex items-center">

        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "radial-gradient(#d6c18a 1px, transparent 1px)",
            backgroundSize: "28px 28px"
          }}
        />

        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 55, repeat: Infinity, ease: "linear" }}
          className="absolute -right-36 -top-48 w-[600px] h-[600px] rounded-full border border-amber-200/20"
        />

        <div className="absolute right-0 bottom-0 w-96 h-96 bg-emerald-400/20 blur-[120px] rounded-full" />

        <div className="relative max-w-7xl mx-auto px-6 py-24 w-full grid lg:grid-cols-2 gap-12 items-center">

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-amber-200 text-sm mb-7">
              <Sparkles size={16} />
              Bandarban University
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
              Accounts &
              <span className="block text-amber-300">
                Finance Division
              </span>
            </h1>

            <p className="text-gray-300 text-lg leading-8 mt-6 max-w-xl">
              Ensuring financial transparency, accountability and
              operational excellence through responsible financial
              management and efficient institutional services.
            </p>

            <div className="flex flex-wrap gap-4 mt-9">

              <a
                href="#officer"
                className="px-7 py-3.5 rounded-xl bg-amber-300 text-[#073b2a] font-bold hover:bg-white transition flex items-center gap-2"
              >
                Meet Our Officer
                <ArrowUpRight size={19} />
              </a>

              <a
                href="#services"
                className="px-7 py-3.5 rounded-xl border border-white/30 hover:bg-white/10 transition"
              >
                Explore Services
              </a>

            </div>
          </motion.div>

          {/* FINANCE GRAPHIC */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            className="hidden lg:flex justify-center"
          >

            <div className="relative w-[360px] h-[360px] flex items-center justify-center">

              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 rounded-full border border-dashed border-amber-200/40"
              />

              <div className="absolute inset-8 rounded-full border border-white/10" />

              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="relative w-64 h-64 rounded-[40px] bg-gradient-to-br from-emerald-300 to-teal-700 shadow-2xl flex flex-col items-center justify-center"
              >
                <Landmark size={85} className="text-white mb-4" />
                <h3 className="text-2xl font-bold">Financial Trust</h3>
                <p className="text-sm text-white/80 mt-1">
                  Transparency & Accountability
                </p>
              </motion.div>

              {[
                { Icon: Calculator, pos: "top-2 left-8" },
                { Icon: Wallet, pos: "top-8 right-4" },
                { Icon: PieChart, pos: "bottom-4 left-8" },
                { Icon: TrendingUp, pos: "bottom-5 right-5" }
              ].map(({ Icon, pos }, i) => (
                <motion.div
                  key={i}
                  animate={{ y: [0, -10, 0] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: i * 0.4
                  }}
                  className={`absolute ${pos} w-14 h-14 rounded-2xl bg-white text-emerald-800 shadow-xl flex items-center justify-center`}
                >
                  <Icon size={25} />
                </motion.div>
              ))}

            </div>
          </motion.div>

        </div>
      </section>

      {/* HIGHLIGHT STRIP */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 -mt-8">

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">

          {[
            {
              icon: ShieldCheck,
              title: "Financial Integrity",
              text: "Trust & Accountability"
            },
            {
              icon: Calculator,
              title: "Accurate Accounting",
              text: "Reliable Financial Records"
            },
            {
              icon: Landmark,
              title: "Institutional Finance",
              text: "Responsible Management"
            }
          ].map((item, i) => (
            <div key={i} className="p-6 flex items-center gap-4">

              <div className="w-12 h-12 shrink-0 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                <item.icon size={24} />
              </div>

              <div>
                <h3 className="font-bold">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.text}</p>
              </div>

            </div>
          ))}

        </div>
      </div>
		
		{/* NEW OFFICER PROFILE - MOHAMMAD SAIMUR RAHAMAN SIFAT */}
		<section id="saimur-profile" className="py-16 px-6 bg-[#f1f7f3]">
		  <div className="max-w-7xl mx-auto">

			{/* Section Heading */}
			<motion.div
			  initial={{ opacity: 0, y: 30 }}
			  whileInView={{ opacity: 1, y: 0 }}
			  viewport={{ once: true }}
			  transition={{ duration: 0.7 }}
			  className="text-center mb-12"
			>
			  <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
				Our Team
			  </span>

			  <h2 className="text-3xl md:text-4xl font-extrabold mt-3 text-gray-900">
				Meet Our Accounts & Finance Officer
			  </h2>

			  <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
				Dedicated to supporting financial administration,
				accounting operations, and institutional excellence.
			  </p>
			</motion.div>

			{/* Officer Card */}
			<motion.div
			  initial={{ opacity: 0, y: 45 }}
			  whileInView={{ opacity: 1, y: 0 }}
			  viewport={{ once: true }}
			  transition={{ duration: 0.8 }}
			  whileHover={{ y: -5 }}
			  className="max-w-5xl mx-auto grid lg:grid-cols-5
				bg-white rounded-[30px] shadow-xl overflow-hidden
				border border-gray-100"
			>

			  {/* PHOTO */}
			  <div className="lg:col-span-2 relative min-h-[420px]
				bg-gradient-to-br from-emerald-900 to-teal-700">

				<div
				  className="absolute inset-0 opacity-20"
				  style={{
					backgroundImage:
					  "radial-gradient(white 1px, transparent 1px)",
					backgroundSize: "20px 20px"
				  }}
				/>

				<img
				  src="/assets/img/sifat.jpeg"
				  alt="Mohammad Saimur Rahaman Sifat"
				  className="relative w-full h-full min-h-[420px]
					object-cover object-top"
				  loading="lazy"
				/>

				{/* Image Overlay */}
				<div className="absolute bottom-5 left-5 right-5
				  p-5 rounded-2xl bg-white/90 backdrop-blur-md">

				  <p className="font-bold text-gray-900 text-lg">
					Mohammad Saimur Rahaman Sifat
				  </p>

				  <p className="text-sm text-emerald-800 font-semibold">
					Assistant Accounts Officer
				  </p>
				</div>
			  </div>

			  {/* DETAILS */}
			  <div className="lg:col-span-3 p-7 md:p-12">

				<span className="inline-flex items-center gap-2
				  text-emerald-700 text-sm font-bold
				  uppercase tracking-wider">

				  <Landmark size={19} />
				  Accounts & Finance Division
				</span>

				<h2 className="text-3xl md:text-4xl font-extrabold
				  text-gray-900 mt-5 leading-tight">

				  Mohammad Saimur Rahaman Sifat
				</h2>

				<p className="text-emerald-700 font-semibold mt-3 text-lg">
				  Assistant Accounts Officer
				</p>

				<p className="text-gray-500 font-medium mt-1">
				  Bandarban University
				</p>

				<div className="h-1 w-20 bg-amber-400 rounded-full mt-7" />

				<p className="text-gray-600 leading-8 mt-7">
				  As an Assistant Accounts Officer in the Accounts & Finance
				  Division, Mohammad Saimur Rahaman Sifat contributes to the
				  university's financial administration, accounting activities,
				  and institutional financial operations. He supports accurate
				  financial recordkeeping and efficient financial services
				  for Bandarban University.
				</p>

				{/* CONTACT INFORMATION */}
				<div className="grid sm:grid-cols-2 gap-4 mt-8">

				  {/* Mobile */}
				  <a
					href="tel:+8801754353917"
					className="flex items-center gap-3 p-4 rounded-xl
					  bg-gray-50 hover:bg-emerald-50
					  border border-gray-100 hover:border-emerald-200
					  transition duration-300"
				  >
					<div className="w-11 h-11 shrink-0 rounded-xl
					  bg-emerald-100 text-emerald-700
					  flex items-center justify-center">

					  <Phone size={21} />
					</div>

					<div>
					  <p className="text-xs text-gray-500">
						Mobile Number
					  </p>

					  <p className="font-semibold text-sm text-gray-800">
						01754353917
					  </p>
					</div>
				  </a>

				  {/* Email */}
				  <a
					href="mailto:saimrahaman20@gmail.com"
					className="flex items-center gap-3 p-4 rounded-xl
					  bg-gray-50 hover:bg-emerald-50
					  border border-gray-100 hover:border-emerald-200
					  transition duration-300"
				  >
					<div className="w-11 h-11 shrink-0 rounded-xl
					  bg-emerald-100 text-emerald-700
					  flex items-center justify-center">

					  <Mail size={21} />
					</div>

					<div className="min-w-0">
					  <p className="text-xs text-gray-500">
						Email Address
					  </p>

					  <p className="font-semibold text-sm text-gray-800 break-all">
						saimrahaman20@gmail.com
					  </p>
					</div>
				  </a>

				</div>

				{/* CONTACT BUTTON */}
				<a
				  href="tel:+8801754353917"
				  className="inline-flex items-center gap-2 mt-7
					px-6 py-3 rounded-xl bg-[#0b4933]
					text-white font-bold
					hover:bg-emerald-600
					hover:shadow-lg transition duration-300"
				>
				  Contact Officer
				  <ArrowUpRight size={18} />
				</a>

			  </div>

			</motion.div>

		  </div>
		</section>

      {/* OFFICER PROFILE */}
      <section id="officer" className="py-24 px-6">

        <div className="max-w-7xl mx-auto">

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-14"
          >

            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              Our Team
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold mt-3 text-gray-900">
              Meet Our Accounts Officer
            </h2>

            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
              Dedicated to maintaining accurate financial records
              and providing efficient financial administration.
            </p>

          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 45 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto grid lg:grid-cols-5 bg-white rounded-[30px] shadow-xl overflow-hidden border border-gray-100"
          >

            {/* PHOTO */}
            <div className="lg:col-span-2 relative min-h-[420px] bg-gradient-to-br from-emerald-900 to-teal-700">

              <div
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage: "radial-gradient(white 1px, transparent 1px)",
                  backgroundSize: "20px 20px"
                }}
              />

              <img
                src="/assets/img/gias.jpeg"
                alt="Md. Gias Uddin"
                className="relative w-full h-full min-h-[420px] object-cover object-top"
              />

              <div className="absolute bottom-5 left-5 right-5 p-5 rounded-2xl bg-white/90 backdrop-blur-md">

                <p className="font-bold text-gray-900 text-lg">
                  Md. Gias Uddin
                </p>

                <p className="text-sm text-emerald-800 font-semibold">
                  Junior Assistant Accounts Officer
                </p>

              </div>

            </div>

            {/* DETAILS */}
            <div className="lg:col-span-3 p-7 md:p-12">

              <span className="inline-flex items-center gap-2 text-emerald-700 text-sm font-bold uppercase tracking-wider">
                <Landmark size={19} />
                Accounts & Finance
              </span>

              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-5">
                Md. Gias Uddin
              </h2>

              <p className="text-emerald-700 font-semibold mt-3 text-lg">
                Junior Assistant Accounts Officer
              </p>

              <p className="text-gray-500 font-medium mt-1">
                Bandarban University
              </p>

              <div className="h-1 w-20 bg-amber-400 rounded-full mt-7" />

              <p className="text-gray-600 leading-8 mt-7">
                The Accounts and Finance Division plays an important
                role in supporting the financial and administrative
                operations of Bandarban University. The division
                focuses on maintaining financial records, facilitating
                accounting activities and supporting transparent
                institutional financial management.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mt-8">

                <a
                  href="tel:+8801874320195"
                  className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-emerald-50 transition"
                >
                  <Phone className="text-emerald-700 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">Phone Number</p>
                    <p className="font-semibold text-sm">01874320195</p>
                  </div>
                </a>

                <a
                  href="mailto:mdgiasuddinsagor1993@gmail.com"
                  className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-emerald-50 transition"
                >
                  <Mail className="text-emerald-700 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500">Email Address</p>
                    <p className="font-semibold text-sm break-all">
                      mdgiasuddinsagor1993@gmail.com
                    </p>
                  </div>
                </a>

              </div>

              <a
                href="tel:+8801874320195"
                className="inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-xl bg-[#0b4933] text-white font-bold hover:bg-emerald-600 transition"
              >
                Contact Officer
                <ArrowUpRight size={18} />
              </a>

            </div>
          </motion.div>
        </div>
      </section>

      {/* FINANCIAL SERVICES */}
      <section id="services" className="py-24 px-6 bg-white">

        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-14">

            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              What We Do
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold mt-3">
              Financial Administration & Services
            </h2>

            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
              Supporting university operations through organized
              accounting, financial administration and institutional
              financial services.
            </p>

          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">

            {services.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                whileHover={{ y: -8 }}
                className="group p-7 rounded-2xl border border-gray-100 bg-[#f8fbf9] hover:bg-[#0b4933] hover:text-white hover:shadow-2xl transition duration-300"
              >

                <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center group-hover:bg-white/20 group-hover:text-white transition">
                  <item.icon size={27} />
                </div>

                <h3 className="text-xl font-bold mt-6">
                  {item.title}
                </h3>

                <p className="text-gray-500 group-hover:text-gray-200 mt-3 leading-7 text-sm">
                  {item.description}
                </p>

                <div className="mt-6 text-emerald-700 group-hover:text-amber-300 flex items-center gap-2 text-sm font-bold">
                  Finance & Accounts
                  <ChevronRight size={17} />
                </div>

              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* FINANCIAL VALUES BANNER */}
      <section className="px-6 py-16 bg-[#f1f7f3]">

        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6">

          {[
            {
              icon: BadgeCheck,
              title: "Transparency",
              text: "Promoting openness in financial administration."
            },
            {
              icon: ShieldCheck,
              title: "Accountability",
              text: "Maintaining responsible financial practices."
            },
            {
              icon: TrendingUp,
              title: "Efficiency",
              text: "Supporting effective institutional operations."
            }
          ].map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              className="flex items-center gap-5 p-6 bg-white rounded-2xl shadow-sm hover:shadow-lg transition"
            >

              <div className="w-14 h-14 shrink-0 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center">
                <item.icon size={27} />
              </div>

              <div>
                <h3 className="font-bold text-lg">{item.title}</h3>
                <p className="text-gray-500 text-sm mt-1">{item.text}</p>
              </div>

            </motion.div>
          ))}

        </div>
      </section>

      {/* VISION MISSION GOALS */}
      <section className="py-24 px-6 bg-white">

        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-14">

            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              Our Direction
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold mt-3">
              Vision, Mission & Goals
            </h2>

          </div>

          <div className="grid md:grid-cols-3 gap-7">

            {principles.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                whileHover={{ y: -8 }}
                className="bg-[#f8fbf9] p-8 rounded-3xl shadow-md hover:shadow-xl transition"
              >

                <div className={`w-14 h-14 ${item.color} rounded-2xl text-white flex items-center justify-center`}>
                  <item.icon size={28} />
                </div>

                <h3 className="text-2xl font-bold mt-6">
                  {item.title}
                </h3>

                <p className="text-gray-500 leading-8 mt-4">
                  {item.description}
                </p>

              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section id="contact" className="relative overflow-hidden bg-[#073b2a] text-white py-20 px-6">

        <div className="absolute w-96 h-96 rounded-full bg-emerald-400/10 blur-3xl -right-20 -top-20" />

        <div className="relative max-w-7xl mx-auto">

          <div className="grid lg:grid-cols-2 gap-12 items-center">

            <div>

              <span className="text-amber-300 font-bold uppercase tracking-[3px] text-sm">
                Get In Touch
              </span>

              <h2 className="text-3xl md:text-5xl font-extrabold mt-4 leading-tight">
                Responsible Finance, Stronger University
              </h2>

              <p className="text-gray-300 mt-5 leading-8">
                Contact the Accounts and Finance Division for
                financial inquiries, accounting information and
                institutional financial assistance.
              </p>

              <a
                href="mailto:acfd@bubban.ac.bd"
                className="inline-flex items-center gap-3 mt-8 bg-amber-300 text-[#073b2a] font-bold px-7 py-4 rounded-xl hover:bg-white transition"
              >
                Contact Finance Office
                <ArrowUpRight size={20} />
              </a>

            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/10 rounded-3xl p-7 space-y-6">

              <div className="flex gap-4 items-start">
                <MapPin className="text-amber-300 shrink-0" size={23} />
                <div>
                  <h4 className="font-bold">University Address</h4>
                  <p className="text-gray-300 text-sm mt-2 leading-7">
                    K.B Road, Shualok, Post Office: Shualok - 4600,
                    Bandarban Sadar, Bangladesh
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-center">
                <Phone className="text-amber-300 shrink-0" />
                <div>
                  <h4 className="font-bold">Office Phone</h4>
                  <a href="tel:+8801318305900" className="text-gray-300 text-sm">
                    +880 1318-305900
                  </a>
                </div>
              </div>

              <div className="flex gap-4 items-center">
                <Mail className="text-amber-300 shrink-0" />
                <div>
                  <h4 className="font-bold">Official Email</h4>
                  <a href="mailto:acfd@bubban.ac.bd" className="text-gray-300 text-sm">
                    acfd@bubban.ac.bd
                  </a>
                </div>
              </div>

              <div className="border-t border-white/20 pt-5">
                <p className="text-xs text-gray-400">
                  ACCOUNTS AND FINANCE DIVISION
                </p>
                <p className="font-bold mt-1">
                  Bandarban University
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
